import { Box, Card, Text, BlockStack, InlineStack, Badge } from '@shopify/polaris';
import type { Colour } from '../types';
import { getScoreTierConfig } from '../theme';
import { AnimatedCounter } from './ui';
import type { ReactNode } from 'react';

/**
 * Circular progress ring around the score value.
 * Renders an SVG with a background track and a coloured arc proportional to `percent` (0-100).
 */
interface ScoreRingProps {
  percent: number;
  colour: string;
  size?: number;
  strokeWidth?: number;
  children: ReactNode;
}

function ScoreRing({
  percent,
  colour,
  size = 120,
  strokeWidth = 6,
  children,
}: Readonly<ScoreRingProps>) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (Math.min(Math.max(percent, 0), 100) / 100) * circumference;

  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        {/* Background track */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="var(--p-color-border-secondary)"
          strokeWidth={strokeWidth}
        />
        {/* Progress arc */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={colour}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 0.8s ease' }}
        />
      </svg>
      {/* Centered score text */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {children}
      </div>
    </div>
  );
}

/**
 * T036: ScoreCard component (with score tier).
 * Displays overall score 0–100, tier badge (AI-Invisible / AI-Confusing / AI-Readable / AI-Discoverable),
 * colour-coded background, product count.
 * Uses theme tokens for colours and animated counter.
 *
 * @see documents/ui-enhancement-spec.md §10.5, §13
 */
interface ScoreCardProps {
  score: number;
  colour: Colour;
  productCount: number;
  storeName?: string;
  /** Enable animated score counter (default: true) */
  animated?: boolean;
  /** Optional content rendered at the bottom of the card */
  children?: ReactNode;
  /** @deprecated Kept for backward compat. Tier is computed from score. */
  grade?: string;
  /** @deprecated Kept for backward compat. Tier is computed from score. */
  tierLabel?: string;
}

export function ScoreCard({
  score,
  colour: _colour,
  productCount,
  storeName,
  animated = true,
  children,
}: Readonly<ScoreCardProps>) {
  const tierConfig = getScoreTierConfig(score);

  // Empty state: no products scanned yet
  if (productCount === 0) {
    return (
      <Card>
        <BlockStack gap="400">
          {storeName && (
            <Text as="h2" variant="headingMd">
              {storeName}
            </Text>
          )}
          <Box
            background="bg-surface-secondary"
            borderRadius="300"
            padding="400"
          >
            <BlockStack gap="200" inlineAlign="center">
              <Text as="p" variant="bodyLg" fontWeight="semibold">
                No score available
              </Text>
              <Text as="p" variant="bodySm" tone="subdued">
                Run a scan to see your AI discoverability score
              </Text>
            </BlockStack>
          </Box>
        </BlockStack>
      </Card>
    );
  }

  return (
    <Card>
      <BlockStack gap="400">
        {storeName && (
          <Text as="h2" variant="headingMd">
            {storeName}
          </Text>
        )}

        <BlockStack gap="300" inlineAlign="center">
          <ScoreRing percent={score} colour={tierConfig.cssVar}>
            {animated ? (
              <AnimatedCounter
                value={score}
                variant="headingXl"
                fontWeight="bold"
                style={{ color: tierConfig.cssVar }}
              />
            ) : (
              <Text as="span" variant="headingXl" fontWeight="bold">
                <span style={{ color: tierConfig.cssVar }}>{score}</span>
              </Text>
            )}
          </ScoreRing>

          <InlineStack gap="200" align="center">
            <Badge tone={tierConfig.badgeTone}>
              {tierConfig.label}
            </Badge>
          </InlineStack>

          <Text as="span" variant="bodySm" tone="subdued">
            AI Discoverability Score
          </Text>

          <Text as="span" variant="bodySm" tone="subdued">
            {productCount} product{productCount === 1 ? '' : 's'} scanned
          </Text>
        </BlockStack>

        {children}
      </BlockStack>
    </Card>
  );
}
